import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import StandardPrintingHeader from './StandardPrintingHeader';
import StandardPrintingSidebar from './StandardPrintingSidebar';
import StandardPrintingContent from './StandardPrintingContent';
import {
  usePrintQuestionsMutation,
  useGetPrintSettingsMutation
} from '../../../../../features/authentication/assignApiSlice';
import { useGetAssignmentPrintMessageMutation } from '../../../../../features/authentication/persistApiSlice';
import AssignmentPrintMessage from '../../../components/AsssignmentPrintMessage';
import { useAddPrintSettingsMutation } from '../../../../../features/authentication/assignmentApislice';
import CustomToast from '../../../../../components/Toast';
import { parseHtml, removeCheckedFromCheckboxes, removeCheckedFromRadioButton, removedropContainerChilds, removeStyleFromCheckedNext, replaceCheckedRadiosWithTick } from '../utils/StandardPrintFunctions';

const StandardPrinting = () => {
  const location = useLocation();
  const { assignmentId, assignmentName, languageId } = location.state;
  const [printAssignmentQuestions, { isLoading: isLoading }] = usePrintQuestionsMutation();
  const [printSettings] = useGetPrintSettingsMutation();
  const [initialQuestionData, setInitialQuestionData] = useState([]);
  const [sequenceId, setSequenceId] = useState([]);
  const [settingsData, setSettingsData] = useState({});
  const [dragged, setDragged] = useState(false);
  const [getPrintMessage] = useGetAssignmentPrintMessageMutation();
  const [gotoId, setGotoId] = useState(null);
  const [selectedAnswerOptions, setSelectedAnswerOptions] = useState([]);
  const [openPrintMessageModal, setOpenPrintMessageModal] = useState(false);
  const [isTwoColumnStack, setIsTwoColumnStack] = useState(false);
  const [addPrintSettings] = useAddPrintSettingsMutation();
  const [questionSettings, setQuestionSettings] = useState(null);
  const [contentHeights, setContentHeights] = useState([]);
  const [hideDuplicate, setHideDuplicate] = useState(false);
  const standardPageHeight = 1000;
  const [originalQuestionsData, setOriginalQuestionsData] = useState([]);
  const [originalSettingsData, setOriginalSettingsData] = useState({});
  const [gotoThumbnailId, setGotoThumbnailId] = useState(null);
  const [isInitialLayoutUpdated, setIsInitialLayoutUpdated] = useState(false);
  const [currentSessionData, setCurrentSessionData] = useState([]);
  const scrollZoneHeight = 150;
  const scrollSpeed = 20;

  useEffect(() => {
    if (assignmentId>0) {
      getPrintAssignmentQuestionsAsync();
    }
  }, [assignmentId]);

  const updateSettings = (data) => {
    setSettingsData({ ...settingsData, ...data });
    addPrintSettingsAsync({ ...settingsData, ...data });
  };

  function getQuestionSettingVariable(key) {
    const qSettings = {
      qAnswerAbove: 'aa',
      qAnswerBelow: 'ab',
      qExclude: 'ex',
      qMcToggle: 'ol',
      qBreakAbove: 'pa',
      qBreakBelow: 'pb',
      qTwoColumn: 'tc',
      qStackLeft: 'sl',
      qStackRight: 'sr',
      qUnstack: 'us',
      qImgSize: 'imgsize',
      qRuleLine: 'rl',
      qWorkspace: 'aw',
      qEndStack: 'es',
      qInlineAwr: 'ia'
    };

    return qSettings[key] || null;
  }

  const createQuestionSettings = (questions) => {
    let quesSettings = [];
    quesSettings = questions.map((q, index) => ({
      questionid: q.questionId,
      settings: [],
      sequence: index + 1
    }));

    setQuestionSettings(quesSettings);
    return quesSettings;
  };

  const stackQuestions = (isRestack = false, updatedQuestionData = []) => {
    let questionData = (isRestack ? updatedQuestionData : initialQuestionData).map(q => {
      if (!isRestack) {
      // eslint-disable-next-line no-unused-vars
        const { stackNo, position, isEndStack, ...rest } = q;
        const cleanedLayoutOptions = (q.layoutOptions || []).filter(
          opt => !['stackLeft', 'stackRight', 'endStack'].includes(opt)
        );
        return { ...rest, layoutOptions: cleanedLayoutOptions };
      }
      return q;
    });

    setInitialQuestionData(questionData);

    if (isRestack) {
      const questionIdOrder = questionData.map(q => q.questionId);
      const updatedSettings = questionIdOrder.map((qId, index) => {
        const existingSetting = settingsData?.questions.find(qs => qs.questionid === qId);
        if (!existingSetting) return null;
        return {
          ...existingSetting,
          sequence: index + 1,
        };
      }).filter(Boolean);

      setQuestionSettings(updatedSettings);
      updateSettings({ questions: updatedSettings });
      CustomToast('Two Column Stack Updated Successfully.', 'info');
      return;
    }

    let qSettings = (questionSettings?.length > 0 && !isRestack)
      ? [...questionSettings].sort((a, b) => a.sequence - b.sequence)
        .map(qs => ({ ...qs, settings: (qs.settings || []).filter(s => !['es', 'sl', 'sr'].includes(s.name)) })) : createQuestionSettings(questionData);
    let leftHeight = 0;
    let rightHeight = 0;
    let currentSide = 'left';
    let headerHeight = getContentHeight('header');
    let isFirstStack = true;

    qSettings.forEach((qs, index) => {
      const questionHeight = getContentHeight(qs.questionid);
      let pageHeight = standardPageHeight;

      if (isFirstStack) {
        pageHeight -= headerHeight;
      }
      if (currentSide === 'left') {
        if (leftHeight === 0) {
          leftHeight += questionHeight;
          qs.settings.push({ name: getQuestionSettingVariable('qStackLeft'), setting: null });
        } else if (leftHeight + questionHeight <= pageHeight) {
          leftHeight += questionHeight;
          qs.settings.push({ name: getQuestionSettingVariable('qStackLeft'), setting: null });
        } else if (leftHeight + questionHeight > pageHeight) {
          currentSide = 'right';
          rightHeight = questionHeight;
          qs.settings.push({ name: getQuestionSettingVariable('qStackRight'), setting: null });
        }
      } else if (currentSide === 'right') {
        if (rightHeight + questionHeight <= pageHeight) {
          rightHeight += questionHeight;
          qs.settings.push({ name: getQuestionSettingVariable('qStackRight'), setting: null });
        } else {
          if (index !== 0) {
            qSettings[index - 1].settings.push({ name: getQuestionSettingVariable('qEndStack'), setting: null });
          }
          leftHeight = questionHeight;
          rightHeight = 0;
          currentSide = 'left';
          qs.settings.push({ name: getQuestionSettingVariable('qStackLeft'), setting: null });
          isFirstStack = false;
        }
      }
    });

    if (qSettings.length > 0) {
      qSettings[qSettings.length - 1].settings.push({ name: getQuestionSettingVariable('qEndStack'), setting: null });
    }
    setIsTwoColumnStack(true);
    setQuestionSettings(qSettings);
    updateSettings({ questions: qSettings });
    CustomToast('Two Column Stack Created Successfully.', 'info');
  };

  const getContentHeight = (questionId) => {
    const question = contentHeights.find(q => q.questionId === questionId);
    return question ? question.height : 0;
  };

  useEffect(() => {
    if (contentHeights.length > 0) {
      setHideDuplicate(true);
    }
  }, [contentHeights]);

  const getPrintSettingsAsync = async () => {
    const options = {
      assignmentId: assignmentId
    };

    const printSettingsResponse = await printSettings(options).unwrap();
    let settings = {
      fontStyleData: printSettingsResponse.fontstyle,
      fontSizeData: printSettingsResponse.fontsize,
      mcSpacingData: printSettingsResponse.mcspacing,
      mcStylingData: printSettingsResponse.mcstyle,
      teacherName: printSettingsResponse.teachername,
      displayAnswer: printSettingsResponse.displayanswer,
      dueDate: printSettingsResponse.duedate,
      crAnswerSpace: printSettingsResponse.cranswerspace,
      fiAnswerSpace: printSettingsResponse.fianswerspace,
      mcAnswerSpace: printSettingsResponse.mcanswerspace,
      instructions: printSettingsResponse.instructions,
      showAssignment: printSettingsResponse.showassignment,
      showClass: printSettingsResponse.showclass,
      showName: printSettingsResponse.showname,
      showTeacher: printSettingsResponse.showteacher,
      answerKey: printSettingsResponse.answerkey,
      questions: printSettingsResponse.questions
    };

    setOriginalSettingsData(settings);
    setSettingsData({ ...settingsData, ...settings });
    setAnswerOptions(printSettingsResponse);
  };

  const setAnswerOptions = (printSettings) => {
    if (printSettings.displayanswer)
      selectedAnswerOptions.push(1);
    if (printSettings.cranswerspace)
      selectedAnswerOptions.push(2);
    if (printSettings.mcanswerspace)
      selectedAnswerOptions.push(3);
    if (printSettings.fianswerspace)
      selectedAnswerOptions.push(4);
  };

  useEffect(() => {
    if (initialQuestionData.length == 0 || !settingsData || isInitialLayoutUpdated) return;

    let updatedQuestionData = setQuestionLayout();
    updatedQuestionData = setQuestionNumber(updatedQuestionData);
    updatedQuestionData = setStackNumber(updatedQuestionData);

    setInitialQuestionData(updatedQuestionData);
    setQuestionSettings(settingsData?.questions);
    setCurrentSessionData(updatedQuestionData);
    setIsInitialLayoutUpdated(true);
  }, [initialQuestionData, settingsData?.questions]);

  const setQuestionLayout = () => {
    const settingsMap = new Map();
    settingsData.questions?.forEach((s) => {
      settingsMap.set(s.questionid, s.settings || []);
    });

    const updatedQuestionData = initialQuestionData.map((q) => {
      const qSettings = settingsMap.get(q.questionId) || [];
      let mcToggleOption = null;
      let position = null;
      let isEndStack = null;
      let isExcluded = null;

      const layoutOptions = qSettings.map((s) => {
        switch (s.name) {
        case getQuestionSettingVariable('qAnswerAbove'):
          return 'answerAbove';
        case getQuestionSettingVariable('qAnswerBelow'):
          return 'answerBelow';
        case getQuestionSettingVariable('qBreakAbove'):
          return 'pageBreakAbove';
        case getQuestionSettingVariable('qBreakBelow'):
          return 'pageBreakBelow';
        case getQuestionSettingVariable('qRuleLine'):
          return 'ruleLine';
        case getQuestionSettingVariable('qWorkspace'):
          return 'workspace';
        case getQuestionSettingVariable('qMcToggle'):
          mcToggleOption = Number(s.setting);
          return 'mcToggleOptions';
        case getQuestionSettingVariable('qStackLeft'):
          position = 'left';
          return 'stackLeft';
        case getQuestionSettingVariable('qStackRight'):
          position = 'right';
          return 'stackRight';
        case getQuestionSettingVariable('qEndStack'):
          isEndStack = true;
          return;
        case getQuestionSettingVariable('qExclude'):
          isExcluded = true;
          return 'exclude';
        default:
          return undefined;
        }
      }).filter(Boolean);

      return {
        ...q, layoutOptions, ...(mcToggleOption != null ? { mcToggleOption } : {}), ...(position != null ? { position } : {}),
        ...(isEndStack == true ? { isEndStack } : {}), ...(isExcluded != null ? { isExcluded } : {})
      };
    });

    return updatedQuestionData;
  };

  const setQuestionNumber = (questionData) => {
    const sequenceMap = new Map(
      (settingsData?.questions || []).map((q) => [q.questionid, q.sequence])
    );

    const hasExcluded = questionData.some((q) => q.isExcluded);
    let questionNo = 1;

    if (hasExcluded) {
      const updatedQuestionData = questionData.map((q) => {
        if (q.isExcluded) {
          return { ...q, questionNo: null };
        }

        const updated = { ...q, questionNo };
        questionNo++;
        return updated;
      });

      return updatedQuestionData;
    }

    const updatedQuestionData = questionData.map((q, index) => {
      const sequence = sequenceMap.get(q.questionId);
      return {
        ...q,
        questionNo: sequence || index + 1,
      };
    });

    return [...updatedQuestionData].sort((a, b) => (a.questionNo || 0) - (b.questionNo || 0));
  };

  const setStackNumber = (questionData) => {
    let stackCounter = 0;
    let activeStack = null;

    return questionData.map((q) => {
      if (q.position) {
        if (activeStack === null) {
          stackCounter++;
          activeStack = stackCounter;
        }

        const result = { ...q, stackNo: activeStack };

        if (q.isEndStack) {
          activeStack = null;
        }
        return result;
      }

      return { ...q };
    });
  };

  const getPrintAssignmentQuestionsAsync = async () => {
    if(isLoading) return;
    const options = {
      assignmentId: assignmentId,
      languageId: languageId
    };
    const printAssignmentQuestionsResponse = await printAssignmentQuestions(options).unwrap();
    const updatedData = printAssignmentQuestionsResponse.map(item => ({
      ...item,
      question: {
        ...item.question,
        question: item.question?.question
      }
    }));
    setInitialQuestionData(updatedData);
    setOriginalQuestionsData(updatedData);
    let sequenceIdArr = [];
    sequenceIdArr = printAssignmentQuestionsResponse.map((item) => {
      return item.questionId;
    });
    setSequenceId(sequenceIdArr);
  };

  const addPrintSettingsAsync = async (data) => {
    try {
      const printSettings = {
        fontstyle: data?.fontStyleData,
        fontsize: data?.fontSizeData,
        answerkey: data?.answerKey,
        displayanswer: data?.displayAnswer,
        showname: data?.showName,
        showclass: data?.showClass,
        showassignment: data?.showAssignment,
        showteacher: data?.showTeacher,
        instructions: data?.instructions,
        duedate: data?.dueDate,
        teachername: data?.teacherName,
        mcspacing: data?.mcSpacingData,
        mcstyle: data?.mcStylingData,
        mcanswerspace: data?.mcAnswerSpace,
        fianswerspace: data?.fiAnswerSpace,
        cranswerspace: data?.crAnswerSpace,
        questions: data?.questions
      };

      var options = {
        assignmentId: assignmentId,
        printSettings: printSettings,
      };

      if (dragged) {
        if (sequenceId && sequenceId.length > 0) {
          const params = new URLSearchParams();
          sequenceId.forEach(item => params.append('qidList', item));
          options.params = params;
        }
      }

      await addPrintSettings(options).unwrap();
    } catch (ex) {
      console.log(ex);
    }
  };

  const updateQuestionDetailsOnTileDragDrop = (updatedData) => {
    setInitialQuestionData(updatedData);

    const hasAtLeastOnePosition = initialQuestionData?.some(q => q.position === 'left' || q.position === 'right');
    const hasAtLeastNoPosition = initialQuestionData?.some(q => !q.position);
    const isMixedType = hasAtLeastOnePosition && hasAtLeastNoPosition;

    if (isTwoColumnStack || isMixedType) {
      stackQuestions(true, updatedData);
    } else {
      let sequenceIdArr = [];
      sequenceIdArr = updatedData.map((item) => {
        return item.questionId;
      });
      setSequenceId(sequenceIdArr);
      setDragged(true);
    }
  };

  const getPrintMessageAsync = async () => {
    const response = await getPrintMessage().unwrap();
    setOpenPrintMessageModal(!response.isHiddenPrintMessage);
  };

  useEffect(() => {
    getPrintMessageAsync();
    getPrintSettingsAsync();
  }, []);

  useEffect(() => {
    if (sequenceId.length > 0) {
      addPrintSettingsAsync(settingsData);
    }
  }, [sequenceId]);

  const normalizeQuestionSettings = (questionDetails, questionDataSettings) => {
    let qSettings = questionDataSettings ? [...questionDataSettings] : [];

    const existingMap = new Map();
    qSettings.forEach(q => existingMap.set(q.questionid, q));

    const updatedSettings = questionDetails.map((qd, index) => {
      const existing = existingMap.get(qd.questionId);

      return {
        ...(existing || {}),
        questionid: qd.questionId,
        sequence: index + 1,
        origsequence: existing?.origsequence || 0,
        settings: existing?.settings ? [...existing.settings] : []
      };
    });

    updateSettings({ questions: updatedSettings });
  };

  useEffect(() => {
    if (!isTwoColumnStack || !initialQuestionData?.length) return;

    if (settingsData?.questions.length > 0 && settingsData?.questions.length !== initialQuestionData.length) {
      normalizeQuestionSettings(initialQuestionData, settingsData?.questions || []);
      stackQuestions();
      return;
    }

    const quesSettings = (settingsData?.questions || []).slice().sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    let currentStackNo = 1;
    const updatedQuestionDetails = initialQuestionData.map(q => {
      const qs = quesSettings.find(qs => qs.questionid === q.questionId);
      if (!qs) return q;

      const settings = (qs.settings || []).map(s => s.name);
      const position = settings.includes('sl') ? 'left' : settings.includes('sr') ? 'right' : null;
      const isEndStack = settings.includes('es');
      const questionNo = qs.sequence;

      const layoutOptions = [
        ...(q.layoutOptions || []).filter(
          opt => !['stackLeft', 'stackRight', 'endStack'].includes(opt)
        ),
        ...(position === 'left' ? ['stackLeft'] : []),
        ...(position === 'right' ? ['stackRight'] : []),
        ...(isEndStack ? ['endStack'] : []),
      ];

      const updatedQuestion = { ...q, ...(position != null && { position }), ...(isEndStack == true ? { isEndStack } : {}), questionNo, layoutOptions };

      if (isTwoColumnStack && position) {
        updatedQuestion.stackNo = currentStackNo;
        if (isEndStack)
          currentStackNo++;
      }

      return updatedQuestion;
    });

    setInitialQuestionData(updatedQuestionDetails);
  }, [settingsData?.questions, isTwoColumnStack]);

  const clearCurrentSession = () => {
    setQuestionSettings(null);
    setIsTwoColumnStack(false);
    setInitialQuestionData(currentSessionData);
    setSettingsData(originalSettingsData);
    updateSettings(originalSettingsData);
    CustomToast('Current Session Cleared Successfully.', 'info');
  };

  const resetToDefault = () => {
    setQuestionSettings(null);
    setIsTwoColumnStack(false);
    const updatedQuestionData = originalQuestionsData.map((q, index) => ({
      ...q,
      questionNo: index + 1
    }));
    setInitialQuestionData(updatedQuestionData);
    var updatedSettings = originalSettingsData;
    updatedSettings.questions = [];
    updatedSettings.showTeacher = true;
    updatedSettings.showAssignment = true;
    updatedSettings.showClass = true;
    updatedSettings.showName = true;
    updatedSettings.instructions = '';
    updatedSettings.answerKey = 0;
    updatedSettings.fontSizeData = 12;
    updatedSettings.fontStyleData = 'Times New Roman';
    updatedSettings.displayAnswer = false;
    updatedSettings.mcSpacingData = 1;
    updatedSettings.mcStylingData = 0;
    updatedSettings.crAnswerSpace = false;
    updatedSettings.fiAnswerSpace = false;
    updatedSettings.mcAnswerSpace = false;
    setSettingsData(updatedSettings);
    updateSettings(updatedSettings);
    CustomToast('Successfully Reset to Default Settings.', 'info');
  };

  const updateQuestionDetailsWithQuestionLayout = (optionId, questionId, questionType, isRemoveOption = false) => {
    if (isRemoveOption) {
      updateQuestionDetailsForRemoveOption(optionId, questionId);
      return;
    }

    if (optionId == 'mcToggleOptions' && questionType != 'MC') return;

    const oppositeMap = {
      answerAbove: ['answerBelow'],
      answerBelow: ['answerAbove'],
      pageBreakAbove: ['pageBreakBelow'],
      pageBreakBelow: ['pageBreakAbove'],
      stackLeft: ['stackRight'],
      stackRight: ['stackLeft']
    };

    const oppositeOptions = oppositeMap[optionId] || [];

    const toogleSetting = (currentValue = 0, max = 2) => {
      return (currentValue + 1) % (max + 1);
    };

    const updateLayout = (list) =>
      list.map(q => {
        if (q.questionId !== questionId) return q;

        let layoutOptions = [...(q.layoutOptions || [])].filter(id => !oppositeOptions.includes(id));

        if (!layoutOptions.includes(optionId)) layoutOptions.push(optionId);

        let mcToggleOption = q.mcToggleOption ?? 0;
        if (optionId === 'mcToggleOptions' && q.type === 'MC') {
          mcToggleOption = toogleSetting(mcToggleOption);
        }

        return { ...q, layoutOptions, ...(optionId === 'mcToggleOptions' && q.type === 'MC' ? { mcToggleOption } : {}) };
      });

    let updatedQuestionData = updateLayout(initialQuestionData);

    if (['stackLeft', 'stackRight'].includes(optionId)) {
      updatedQuestionData = handleMoveToStack(optionId, questionId, updatedQuestionData);
      updatedQuestionData = updateQuestionNumber(updatedQuestionData);
    }

    if (optionId == 'exclude')
      updatedQuestionData = handleExclude(updatedQuestionData, questionId);

    setInitialQuestionData(updatedQuestionData);

    let qSettings = settingsData?.questions?.length > 0 ? settingsData.questions : createQuestionSettings(updatedQuestionData);

    let updatedQSettings = qSettings.map((q) => {
      if (q.questionid === questionId) {
        let newSettings = [...(q.settings || [])];

        const addSetting = (key, setting = null) => {
          const name = getQuestionSettingVariable(key);
          if (!newSettings.some((s) => s.name === name)) {

            const settingString = setting != null ? String(setting) : null;
            newSettings.push({ name, setting: settingString });
          }
        };

        const removeSetting = (key) => {
          const name = getQuestionSettingVariable(key);
          newSettings = newSettings.filter((s) => s.name !== name);
        };

        switch (optionId) {
        case 'answerAbove':
          removeSetting('qAnswerBelow');
          addSetting('qAnswerAbove', '');
          break;
        case 'answerBelow':
          removeSetting('qAnswerAbove');
          addSetting('qAnswerBelow', '');
          break;
        case 'pageBreakAbove':
          removeSetting('qBreakBelow');
          addSetting('qBreakAbove');
          break;
        case 'pageBreakBelow':
          removeSetting('qBreakAbove');
          addSetting('qBreakBelow');
          break;
        case 'ruleLine':
          addSetting('qRuleLine');
          break;
        case 'workspace':
          addSetting('qWorkspace');
          break;
        case 'mcToggleOptions':
          removeSetting('qMcToggle');
          addSetting('qMcToggle', updatedQuestionData.find(q => q.questionId === questionId)?.mcToggleOption ?? 0);
          break;
        case 'stackLeft':
          removeSetting('qStackLeft');
          removeSetting('qStackRight');
          addSetting('qStackLeft');
          break;
        case 'stackRight':
          removeSetting('qStackLeft');
          removeSetting('qStackRight');
          addSetting('qStackRight');
          break;
        case 'exclude':
          addSetting('qExclude');
        }

        return { ...q, settings: newSettings };
      }

      return q;
    });

    if (['stackLeft', 'stackRight'].includes(optionId)) {
      updatedQSettings = handleMoveToStackQSettings(questionId, updatedQuestionData, updatedQSettings);
      updatedQSettings = updateQuestionSequence(updatedQuestionData, updatedQSettings);
    }

    setQuestionSettings(updatedQSettings);
    setSettingsData(prev => ({ ...prev, questions: updatedQSettings }));
    updateSettings({ questions: updatedQSettings });
  };

  const handleMoveToStack = (optionId, questionId, updatedQuestionData) => {
    const index = updatedQuestionData.findIndex(q => q.questionId === questionId);
    if (index === -1) return updatedQuestionData;

    const prev = updatedQuestionData[index - 1];
    const next = updatedQuestionData[index + 1];
    const curr = { ...updatedQuestionData[index] };

    const getNewStackNo = (data, insertIndex) => {
      const nums = data.map(q => q.stackNo).filter(n => n != null);
      if (nums.length === 0) return 1;

      const prev = data[insertIndex - 1];
      const next = data[insertIndex + 1];

      if (prev?.stackNo && next?.stackNo && prev.stackNo !== next.stackNo) {
        return (prev.stackNo + next.stackNo) / 2;
      }

      if (next?.stackNo && !prev?.stackNo) {
        const minStackNo = Math.min(...nums);
        return minStackNo > 1 ? minStackNo - 1 : 1;
      }

      if (prev?.stackNo && !next?.stackNo) {
        const maxStackNo = Math.max(...nums);
        return maxStackNo + 1;
      }

      return Math.max(...nums) + 1;
    };

    const newStackNo = getNewStackNo(updatedQuestionData, index);
    let assignedStackNo = prev?.stackNo ?? next?.stackNo ?? newStackNo;

    curr.stackNo = assignedStackNo;
    curr.position = optionId === 'stackLeft' ? 'left' : 'right';

    let sameStackQuestions = updatedQuestionData
      .filter(q => q.stackNo === assignedStackNo)
      .map(q => ({ ...q }));

    const exists = sameStackQuestions.some(q => q.questionId === curr.questionId);
    if (!exists) {
      sameStackQuestions.push({ ...curr, stackNo: assignedStackNo });
    } else {
      sameStackQuestions = sameStackQuestions.map(q =>
        q.questionId === curr.questionId
          ? {
            ...q,
            ...curr,
            stackNo: assignedStackNo,
            position: optionId === 'stackLeft' ? 'left' : 'right',
          }
          : q
      );
    }

    sameStackQuestions.sort((a, b) => {
      if (a.position === b.position) {
        return (a.questionNo ?? 0) - (b.questionNo ?? 0);
      }
      return a.position === 'left' ? -1 : 1;
    });

    sameStackQuestions = sameStackQuestions.map((q, i, arr) => {
      if (i === arr.length - 1) {
        return { ...q, isEndStack: true };
      }

      // eslint-disable-next-line no-unused-vars
      const { isEndStack, ...rest } = q;
      return rest;
    });

    const updatedQuesData = updatedQuestionData.map(q => {
      const updated = sameStackQuestions.find(x => x.questionId === q.questionId);
      return updated ? updated : q;
    });

    return updatedQuesData;
  };

  const updateQuestionNumber = (updatedQuestionData) => {
    const data = [...updatedQuestionData];
    const processedIds = new Set();
    let nextNo = 1;
    const newList = [];

    for (let i = 0; i < data.length; i++) {
      const q = data[i];
      if (processedIds.has(q.questionId)) continue;

      if (q.stackNo != null) {
        const stackItems = data.filter(x => x.stackNo === q.stackNo);
        if (stackItems.length > 0) {
          stackItems.forEach(s => processedIds.add(s.questionId));

          let left = stackItems
            .filter(x => x.position === 'left')
            .sort((a, b) =>
              data.findIndex(d => d.questionId === a.questionId) -
              data.findIndex(d => d.questionId === b.questionId)
            );

          let right = stackItems
            .filter(x => x.position === 'right')
            .sort((a, b) =>
              data.findIndex(d => d.questionId === a.questionId) -
              data.findIndex(d => d.questionId === b.questionId)
            );

          const movedItem = stackItems.find(x =>
            ![...left, ...right].some(y => y.questionId === x.questionId)
          );

          if (movedItem) {
            const fromSide = movedItem.prevPosition;
            const toSide = movedItem.position;

            if (fromSide && toSide && fromSide !== toSide) {
              const fromList = fromSide === 'left' ? left : right;
              const oldIndex = fromList.findIndex(
                x => x.questionId === movedItem.questionId
              );

              const targetList = toSide === 'left' ? left : right;
              const insertIndex =
                oldIndex >= 0 && oldIndex < targetList.length
                  ? oldIndex
                  : targetList.length;

              const updatedTarget = [...targetList];
              updatedTarget.splice(insertIndex, 0, movedItem);

              if (toSide === 'left') left = updatedTarget;
              else right = updatedTarget;
            }
          }

          const orderedStack = [...left, ...right];

          orderedStack.forEach(item => {
            newList.push({ ...item, questionNo: nextNo++ });
          });
          continue;
        }
      }

      newList.push({ ...q, questionNo: nextNo++ });
    }

    return newList;
  };

  const handleMoveToStackQSettings = (questionId, updatedQuestionData, qSettings) => {
    const curr = updatedQuestionData.find(q => q.questionId === questionId);
    if (!curr || curr.stackNo == null) return qSettings;

    const stackNo = curr.stackNo;

    const sameStackQuestions = updatedQuestionData.filter(q => q.stackNo === stackNo);
    const endStackId = sameStackQuestions.at(-1)?.questionId;

    return qSettings.map(q => {
      const newSettings = [...(q.settings || [])];

      const addSetting = (key) => {
        const name = getQuestionSettingVariable(key);
        if (!newSettings.some(s => s.name === name)) {
          newSettings.push({ name, setting: null });
        }
      };

      const removeSetting = (key) => {
        const name = getQuestionSettingVariable(key);
        const index = newSettings.findIndex(s => s.name === name);
        if (index !== -1) newSettings.splice(index, 1);
      };

      if (sameStackQuestions.some(x => x.questionId === q.questionid)) {
        removeSetting('qEndStack');
      }

      if (q.questionid === endStackId) {
        addSetting('qEndStack');
      }

      return { ...q, settings: newSettings };
    });
  };

  const updateQuestionSequence = (updatedQuestionData, qSettings) => {
    const settingsMap = new Map(
      qSettings.map(s => [s.questionid, s])
    );

    const updatedQSettings = updatedQuestionData
      .map((q, index) => {
        const settingItem = settingsMap.get(q.questionId);
        if (!settingItem) return null;

        return {
          ...settingItem,
          sequence: index + 1
        };
      })
      .filter(Boolean);

    return updatedQSettings;
  };

  const handleExclude = (questions, targetQuestionId) => {
    const index = questions.findIndex(q => q.questionId === targetQuestionId);
    if (index === -1) return questions;

    return questions.map((q, i) => {
      if (i === index) {
        return { ...q, questionNo: null };
      } else if (i > index) {
        return { ...q, questionNo: q.questionNo - 1 };
      }
      return q;
    });
  };

  const handleInclude = (questions, targetQuestionId) => {
    const index = questions.findIndex(q => q.questionId === targetQuestionId);
    if (index === -1) return questions;

    const prevQuestionNo = questions[index - 1]?.questionNo || 0;
    let nextNumber = prevQuestionNo + 1;

    return questions.map((q, i) => {
      if (i === index) {
        return { ...q, questionNo: nextNumber++ };
      } else if (i > index && q.questionNo !== null) {
        return { ...q, questionNo: nextNumber++ };
      }
      return q;
    });
  };

  const updateQuestionDetailsForRemoveOption = (optionId, questionId) => {
    const removeOptionMap = {
      removeAnswerAbove: ['answerAbove'],
      removeAnswerBelow: ['answerBelow'],
      removePageBreakAbove: ['pageBreakAbove'],
      removePageBreakBelow: ['pageBreakBelow'],
      removeRuleLine: ['ruleLine'],
      removeWorkspace: ['workspace'],
      unstackLeft: ['stackLeft'],
      unstackRight: ['stackRight'],
      include: ['exclude']
    };

    const optionsToRemove = removeOptionMap[optionId] || [];

    let updatedQuestionData = initialQuestionData.map(q => {
      if (q.questionId === questionId) {
        const layoutOptions = (q.layoutOptions || []).filter(id => !optionsToRemove.includes(id));
        return { ...q, layoutOptions };
      }
      return q;
    });

    if (optionId === 'unstackLeft' || optionId === 'unstackRight') {
      updatedQuestionData = handleUnstack(updatedQuestionData, questionId);
      updatedQuestionData = updateQuestionNumber(updatedQuestionData);
    }

    if (optionId == 'include')
      updatedQuestionData = handleInclude(updatedQuestionData, questionId);

    setInitialQuestionData(updatedQuestionData);

    const qSettings = settingsData?.questions?.length > 0 ? settingsData.questions : createQuestionSettings(updatedQuestionData);

    let updatedQSettings = qSettings.map(q => {
      if (q.questionid === questionId) {
        let newSettings = [...(q.settings || [])];

        const settingKeyMap = {
          removeAnswerAbove: 'qAnswerAbove',
          removeAnswerBelow: 'qAnswerBelow',
          removePageBreakAbove: 'qBreakAbove',
          removePageBreakBelow: 'qBreakBelow',
          removeRuleLine: 'qRuleLine',
          removeWorkspace: 'qWorkspace',
          unstackLeft: 'qStackLeft',
          unstackRight: 'qStackRight',
          include: 'qExclude'
        };

        const settingName = getQuestionSettingVariable(settingKeyMap[optionId]);
        newSettings = newSettings.filter(s => s.name !== settingName);
        return { ...q, settings: newSettings };
      }
      return q;
    });

    if (optionId === 'unstackLeft' || optionId === 'unstackRight') {
      updatedQSettings = handleUnstackQSettings(questionId, updatedQuestionData, updatedQSettings);
      updatedQSettings = updateQuestionSequence(updatedQuestionData, updatedQSettings);
    }

    setQuestionSettings(updatedQSettings);
    setSettingsData(prev => ({ ...prev, questions: updatedQSettings }));
    updateSettings({ questions: updatedQSettings });
  };

  const handleResetImages = (questionId) => {
    let updatedQuestions = initialQuestionData.map((q) => ({
      ...q,
      question: { ...q.question },
    }));

    const targetIndex = updatedQuestions.findIndex(
      (item) => item.questionId === questionId
    );
    if (targetIndex !== -1) {
      let questionHtml = updatedQuestions[targetIndex].question?.question || '';

      questionHtml = questionHtml
        .replace(/\swidth\s*=\s*["'][^"']*["']/gi, '')
        .replace(/\sheight\s*=\s*["'][^"']*["']/gi, '');

      questionHtml = questionHtml.replace(
        /style\s*=\s*["'][^"']*["']/gi,
        (match) => {
          let cleaned = match
            .replace(/\bwidth\s*:\s*[^;]+;?/gi, '')
            .replace(/\bheight\s*:\s*[^;]+;?/gi, '')
            .replace(/style\s*=\s*["']\s*["']/, '');
          return cleaned.trim();
        }
      );

      questionHtml = questionHtml.replace(/\sstyle\s*=\s*["']\s*["']/gi, '');
      updatedQuestions[targetIndex].question.question = questionHtml;
    }
    setInitialQuestionData(updatedQuestions);
  };

  const handleUnstack = (updatedQuestionData, questionId) => {
    const data = [...updatedQuestionData];
    const index = data.findIndex(q => q.questionId === questionId);
    const curr = data[index];

    if (!curr.stackNo) return data;

    const sameStack = data.filter(q => q.stackNo === curr.stackNo);
    const otherStackMembers = sameStack.filter(q => q.questionId !== questionId);

    const filtered = data.filter(q => q.questionId !== questionId);
    // eslint-disable-next-line no-unused-vars
    const { stackNo, position, isEndStack, ...unstacked } = curr;

    if (otherStackMembers.length > 0) {
      const lastMember = otherStackMembers[otherStackMembers.length - 1];
      const lastMemberIndex = filtered.findIndex(q => q.questionId === lastMember.questionId);

      filtered.splice(lastMemberIndex + 1, 0, unstacked);
    } else {
      filtered.splice(index, 0, unstacked);
    }

    const stackGroups = {};
    filtered.forEach(q => {
      if (q.stackNo) {
        if (!stackGroups[q.stackNo]) stackGroups[q.stackNo] = [];
        stackGroups[q.stackNo].push(q);
      }
    });

    Object.values(stackGroups).forEach(stack => {
      const sorted = stack.sort((a, b) => (a.questionNo ?? 0) - (b.questionNo ?? 0));
      const lastItem = sorted[sorted.length - 1];
      const index = filtered.findIndex(q => q.questionId === lastItem.questionId);
      if (index !== -1 && !filtered[index].isEndStack) {
        filtered[index] = { ...filtered[index], isEndStack: true };
      }
    });

    return filtered.map((q, i) => ({ ...q, questionNo: i + 1 }));
  };

  const handleUnstackQSettings = (questionId, updatedQuestionData, qSettings) => {
    const curr = updatedQuestionData.find(q => q.questionId === questionId);
    if (!curr) return qSettings;

    const endStackQuestionIds = updatedQuestionData
      .filter(q => q.isEndStack)
      .map(q => q.questionId);

    return qSettings.map(q => {
      let newSettings = [...(q.settings || [])];

      const removeSetting = (key) => {
        const name = getQuestionSettingVariable(key);
        const index = newSettings.findIndex(s => s.name === name);
        if (index !== -1) newSettings.splice(index, 1);
      };

      const addSetting = (key) => {
        const name = getQuestionSettingVariable(key);
        if (!newSettings.some(s => s.name === name)) {
          newSettings.push({ name, setting: null });
        }
      };

      if (q.questionid === questionId) {
        removeSetting('qStackLeft');
        removeSetting('qStackRight');
        removeSetting('qEndStack');
      }

      if (endStackQuestionIds.includes(q.questionid)) {
        addSetting('qEndStack');
      }

      return { ...q, settings: newSettings };
    });
  };

  const updateQuestionDetailsOnDrag = (updatedQuestionData) => {
    setInitialQuestionData(updatedQuestionData);

    const questionSettings = settingsData?.questions?.length > 0 ? settingsData.questions : createQuestionSettings(updatedQuestionData);
    let updatedQSettings = updateQuestionSettingsOnDrag(updatedQuestionData, questionSettings);

    setQuestionSettings(updatedQSettings);
    setSettingsData(prev => ({ ...prev, questions: updatedQSettings }));
    updateSettings({ questions: updatedQSettings });

  };

  const updateQuestionSettingsOnDrag = (questionData, questionSettings) => {
    if (!questionData?.length || !questionSettings?.length) return questionSettings;

    const stackGroups = questionData.reduce((acc, q) => {
      if (q.stackNo != null) {
        if (!acc[q.stackNo]) acc[q.stackNo] = [];
        acc[q.stackNo].push(q);
      }
      return acc;
    }, {});

    const updatedSettings = questionData.map((q) => {
      const existingSetting = questionSettings.find(
        (qs) => String(qs.questionid) === String(q.questionId)
      );

      let newSettings = [...(existingSetting?.settings || [])];

      const addSetting = (key) => {
        const name = getQuestionSettingVariable(key);
        if (!newSettings.some((s) => s.name === name)) {
          newSettings.push({ name, setting: null });
        }
      };

      const removeSetting = (key) => {
        const name = getQuestionSettingVariable(key);
        newSettings = newSettings.filter((s) => s.name !== name);
      };

      ['qStackLeft', 'qStackRight', 'qEndStack'].forEach(removeSetting);

      if (q.stackNo != null) {
        if (q.position === 'left') addSetting('qStackLeft');
        else if (q.position === 'right') addSetting('qStackRight');

        const stackMembers = stackGroups[q.stackNo] || [];
        const endStackId = stackMembers.at(-1)?.questionId;
        if (q.questionId === endStackId) addSetting('qEndStack');
      }

      return { ...(existingSetting || {}), questionid: q.questionId, sequence: q.questionNo, settings: newSettings };
    });

    return updatedSettings;
  };

  function fixContentAlignment(html, qtype) {
    if (!html) return html;
  
    const standardWidth = 150;
    const maxWidth = 450;
    const hasAtLeastOnePosition = initialQuestionData?.some(q => q.position === 'left' || q.position === 'right');
    const hasAtLeastNoPosition = initialQuestionData?.some(q => !q.position);
    const isMixedType = hasAtLeastOnePosition && hasAtLeastNoPosition;
    const isStackEnabled = (isTwoColumnStack || isMixedType);
    const doc = parseHtml(html);
  
    const imgs = doc.querySelectorAll('img');
    const imgsCount = imgs.length;
  
    imgs.forEach(img => {
      img.removeAttribute('onload');
  
      if (isStackEnabled) {
        const isPartOfTableLayout = !!img.closest('table');
        const imgClosest = img.closest('td, th');
        const hasColspan = imgClosest && imgClosest.hasAttribute('colspan');

        if (isPartOfTableLayout && imgsCount > 1 && !hasColspan) {
          img.style.maxWidth = `${standardWidth}px`;
          img.style.removeProperty('height');
        } else {
          img.style.maxWidth = `${maxWidth}px`;
          img.style.removeProperty('height');
        }
      }
    });
  
    const bookletTables = doc.querySelectorAll('table.booklet');
  
    bookletTables.forEach(table => {
      if (table.getAttribute('width'))
        table.removeAttribute('width');
  
      const innerTables = table.querySelectorAll('table');
      innerTables.forEach(div => {
        const styleWidthMatch = div.style.width?.match(/(\d+)/);
        const widthAttr = div.getAttribute('width');
        let actualWidth = null;
  
        if (styleWidthMatch) {
          actualWidth = parseInt(styleWidthMatch[1], 10);
        } else if (widthAttr && !isNaN(parseInt(widthAttr, 10))) {
          actualWidth = parseInt(widthAttr, 10);
        }
  
        if (actualWidth && actualWidth > maxWidth) {
          if (styleWidthMatch) div.style.removeProperty('width');
          if (widthAttr) div.removeAttribute('width');
        }
      });

      if(isStackEnabled)
      {
        var pages = table.querySelectorAll('div[id^="page"]');
        pages.forEach(div => {
          div.style.maxWidth = `${maxWidth}px`;
        });
      }
    });
  
    const tablesWithWidth = doc.querySelectorAll('table');
  
    tablesWithWidth.forEach(table => {
      if (table.getAttribute('width')) table.removeAttribute('width');
  
      const styleWidthMatch = table.style.width?.match(/(\d+)/);
      if (styleWidthMatch) table.style.removeProperty('width');
    });
      
    if(qtype == 4 || qtype ==5)
      removeStyleFromCheckedNext(doc);
  
    doc.querySelectorAll('a').forEach(a => {
      a.style.setProperty('color', 'blue', 'important');
    });
  
    doc.querySelectorAll('span').forEach(span => {
      if (span.hasAttribute('style')) {
        span.style.removeProperty('font-family');
        span.style.removeProperty('font-size');
      }
    });
  
    doc.querySelectorAll('font').forEach(font => {
      const parent = font.parentNode;
      while (font.firstChild) {
        parent.insertBefore(font.firstChild, font);
      }
      parent.removeChild(font);
    });

    doc.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.removeAttribute('disabled');
    });

    doc.querySelectorAll('input[type="radio"]').forEach(checkbox => {
      checkbox.removeAttribute('disabled');
    });
  
    let finalHtml = doc.body.innerHTML;
  
    if(!selectedAnswerOptions.includes(1)) 
    {
      finalHtml = removedropContainerChilds(finalHtml);
      if(qtype == 5 || qtype == 4){
        finalHtml = removeCheckedFromCheckboxes(finalHtml);
        finalHtml = removeCheckedFromRadioButton(finalHtml);
      }
    } else if (qtype == 4) {
      finalHtml = replaceCheckedRadiosWithTick(finalHtml);
    }
    return finalHtml;
  }

  const autoScroll = (clientY, refElement) => {
    const container = refElement.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const viewHeight = rect.height;
    const dragYPosition = clientY - rect.top;
    let speed = 0;
  
    if (dragYPosition < scrollZoneHeight) {
      const t = Math.max(0, (scrollZoneHeight - dragYPosition) / scrollZoneHeight);
      speed = -scrollSpeed * t * t;
    } else if (dragYPosition > viewHeight - scrollZoneHeight) {
      const t = Math.max(0, (dragYPosition - (viewHeight - scrollZoneHeight)) / scrollZoneHeight);
      speed = scrollSpeed * t * t;
    }
  
    if (speed !== 0) {
      container.scrollTop += speed;
    }
  };

  return (
    <>
      <div className="printing-container">
        <div className="row">
          <div className="col-lg-3">
            <StandardPrintingSidebar
              questionDetails={initialQuestionData}
              updateQuestionDetails={updateQuestionDetailsOnTileDragDrop}
              setGotoId={setGotoId}
              isLoading={isLoading}
              isTwoColumnStack={isTwoColumnStack}
              gotoThumbnailId={gotoThumbnailId}
              fixContentAlignment={fixContentAlignment}
              autoScroll={autoScroll}
            />
          </div>
          <div className="col-lg-9 right-container d-grid">
            <StandardPrintingHeader
              updateSettings={updateSettings}
              fontSizeValue={settingsData?.fontSizeData}
              fontStyleValue={settingsData?.fontStyleData}
              mcSpaceValue={settingsData?.mcSpacingData}
              mcstyleValue={settingsData?.mcStylingData}
              answerKeyValue={settingsData?.answerKey}
              assignmentName={assignmentName}
              assignmentId={assignmentId}
              selectedAnswerOptions={selectedAnswerOptions}
              setSelectedAnswerOptions={setSelectedAnswerOptions}
              settings={settingsData}
              isLoading={isLoading}
              stackQuestions={stackQuestions}
              clearCurrentSession={clearCurrentSession}
              resetToDefault={resetToDefault}
              isTwoColumnStack={isTwoColumnStack}
            />
            <StandardPrintingContent
              assignmentName={assignmentName}
              fontStyleData={settingsData?.fontStyleData}
              fontSizeData={settingsData?.fontSizeData}
              mcSpacingData={settingsData?.mcSpacingData}
              mcStylingData={settingsData?.mcStylingData}
              answerKey={settingsData?.answerKey}
              questionDetails={initialQuestionData}
              teacherNameData={settingsData?.teacherName}
              dueDate={settingsData?.dueDate}
              initialInstructions={settingsData?.instructions}
              updateSettings={updateSettings}
              settingsData={settingsData}
              gotoId={gotoId}
              setGotoId={setGotoId}
              selectedAnswerOptions={selectedAnswerOptions}
              isLoading={isLoading}
              isTwoColumnStack={isTwoColumnStack}
              setIsTwoColumnStack={setIsTwoColumnStack}
              questionSettings={questionSettings ?? settingsData?.questions}
              setContentHeights={setContentHeights}
              hideDuplicate={hideDuplicate}
              updateQuestionDetailsWithQuestionLayout={updateQuestionDetailsWithQuestionLayout}
              setGotoThumbnailId={setGotoThumbnailId}
              handleResetImages={handleResetImages}
              updateQuestionDetailsOnDrag={updateQuestionDetailsOnDrag}
              fixContentAlignment={fixContentAlignment}
              autoScroll={autoScroll}
              standardPageHeight={standardPageHeight}
            />
          </div>
        </div>
      </div>

      {!isLoading && openPrintMessageModal &&
        <AssignmentPrintMessage
          isOpen={openPrintMessageModal}
          onClose={() => { setOpenPrintMessageModal(false); }}
        />
      }
    </>
  );
};

export default StandardPrinting;
